import React from 'react';
import { FadeIn } from './FadeIn';
import { ArrowRight, BookOpen, Search, MessageSquare, ShieldCheck, DollarSign, Bot, CalendarCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { trackAddToCart } from '../utils/metaPixel';
export function FinalCTA() {
  return <section className="py-12 md:py-20 bg-cream-50 overflow-hidden" data-id="element-57">
      <div className="max-w-3xl mx-auto px-6" data-id="element-58">
        <FadeIn data-id="element-59">
          <div className="text-center mb-10" data-id="element-60">
            <h2 className="text-2xl md:text-4xl mb-4" data-id="element-61">
              What Changes After This
            </h2>
            <div className="space-y-1 text-base text-brown-800 mb-5" data-id="element-62">
              <p data-id="element-63">You stop fearing the mailbox.</p>
              <p data-id="element-64">You stop paying blindly.</p>
              <p data-id="element-65">You stop feeling small on billing calls.</p>
            </div>
            <motion.div initial={{
            scale: 0.95,
            opacity: 0
          }} whileInView={{
            scale: 1,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            ease: 'easeOut'
          }} className="bg-white inline-block px-6 py-4 rounded-xl shadow-lg border-2 border-terracotta-200 relative overflow-hidden" data-id="element-66">
              <div className="absolute inset-0 bg-gradient-to-br from-terracotta-50/50 to-transparent pointer-events-none" data-id="element-67"></div>
              <div className="relative z-10" data-id="element-68">
                <p className="text-brown-600 mb-1 text-sm uppercase tracking-wide" data-id="element-69">
                  You become:
                </p>
                <motion.p initial={{
                opacity: 0,
                y: 8
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.6,
                delay: 0.3
              }} className="text-xl md:text-3xl font-bold text-terracotta-600" data-id="element-70">
                  "The woman who audits before she pays."
                </motion.p>
              </div>
              <div className="absolute -top-1 -left-1 w-3 h-3 border-t-2 border-l-2 border-terracotta-400 rounded-tl-lg" data-id="element-71"></div>
              <div className="absolute -top-1 -right-1 w-3 h-3 border-t-2 border-r-2 border-terracotta-400 rounded-tr-lg" data-id="element-72"></div>
              <div className="absolute -bottom-1 -left-1 w-3 h-3 border-b-2 border-l-2 border-terracotta-400 rounded-bl-lg" data-id="element-73"></div>
              <div className="absolute -bottom-1 -right-1 w-3 h-3 border-b-2 border-r-2 border-terracotta-400 rounded-br-lg" data-id="element-74"></div>
            </motion.div>
            <p className="mt-5 text-brown-600 text-sm" data-id="element-75">
              That shift protects your family long-term.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.2} data-id="element-76">
          <div id="what-you-get" className="bg-brown-900 text-cream-50 rounded-3xl p-6 md:p-12 text-center relative shadow-2xl" data-id="element-77">
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-terracotta-500 to-terracotta-600" data-id="element-78"></div>

            <h2 className="text-2xl md:text-4xl mb-2 text-white leading-tight" data-id="element-79">
              What You Get Inside
            </h2>
            <p className="text-sm md:text-base text-terracotta-400 font-semibold mb-4" data-id="element-80">
              The Negotiation Blueprint for Women Who Refuse to Be Overcharged
            </p>
            <p className="text-sm text-cream-200 mb-6 max-w-md mx-auto leading-relaxed" data-id="element-81">
              A step-by-step system to question, negotiate, and reduce medical
              bills with confidence.
            </p>

            <div className="text-left max-w-md mx-auto space-y-3 mb-8" data-id="element-82">
              {[{
              icon: BookOpen,
              title: 'Complete Negotiation Blueprint',
              desc: 'How billing works and how to use it to your advantage.'
            }, {
              icon: Search,
              title: 'Itemized Bill Leverage System',
              desc: 'Request, review, and audit charges before negotiating.'
            }, {
              icon: MessageSquare,
              title: 'Word-for-Word Scripts',
              desc: 'What to say for reductions, settlements, and adjustments.'
            }, {
              icon: ShieldCheck,
              title: 'Insurance Appeal Framework',
              desc: 'Challenge denied or underpaid claims.'
            }, {
              icon: DollarSign,
              title: 'Payment Plan Leverage',
              desc: 'Use timing and lump-sum offers to strengthen your position.'
            }, {
              icon: Bot,
              title: 'AI Expense Tracker Setup',
              desc: 'Organize, flag discrepancies, and negotiate from clarity.'
            }, {
              icon: CalendarCheck,
              title: '30-Day Action Plan',
              desc: 'A clear roadmap so you always know your next step.'
            }].map((item, i) => <div key={i} className="flex items-start gap-2.5" data-id="element-83">
                  <div className="bg-brown-800 p-1.5 rounded-md flex-shrink-0 mt-0.5" data-id="element-84">
                    <item.icon className="w-4 h-4 text-terracotta-400" data-id="element-85" />
                  </div>
                  <div data-id="element-86">
                    <p className="font-bold text-white text-sm" data-id="element-87">{item.title}</p>
                    <p className="text-cream-300 text-xs leading-relaxed" data-id="element-88">
                      {item.desc}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="border-t border-brown-700 pt-6 mb-6 max-w-md mx-auto" data-id="element-89">
              <p className="text-cream-200 text-sm leading-relaxed mb-2" data-id="element-90">
                Everything structured. Everything practical. Built for busy moms
                who don't have time to decode billing codes.
              </p>
              <p className="text-white font-semibold text-base" data-id="element-91">
                Hospitals have systems.{' '}
                <span className="text-terracotta-400" data-id="element-92">Now you do too.</span>
              </p>
            </div>

            <a id="final-cta" href="https://www.cleverpockets.com/cart/44867243868296:1" target="_blank" rel="noopener noreferrer" onClick={e => {
            e.preventDefault();
            trackAddToCart();
            setTimeout(() => {
              window.open('https://www.cleverpockets.com/cart/44867243868296:1', '_blank', 'noopener,noreferrer');
            }, 300);
          }} className="inline-flex items-center justify-center px-8 py-5 text-xl font-bold text-white bg-terracotta-500 rounded-full shadow-lg hover:bg-terracotta-600 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 group" data-id="element-93">
              Get My Blueprint
              <ArrowRight className="ml-2 w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-94" />
            </a>

            <div className="mt-5 flex flex-col items-center gap-2" data-id="element-95">
              <div className="flex items-center gap-3" data-id="element-96">
                <span className="text-lg text-[#FAF6F1] line-through decoration-[#FAF6F1]/60 decoration-2" data-id="element-97">
                  $19.99
                </span>
                <span className="text-4xl font-bold text-green-400" data-id="element-98">$6.39</span>
                <span className="bg-green-500 text-white text-xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-full" data-id="element-99">
                  68% Off
                </span>
              </div>
              <p className="text-cream-300 text-sm" data-id="element-100">
                Get immediate access with a one‑time, secure payment.
              </p>
            </div>

            <p className="mt-4 text-cream-300 text-xs md:text-sm" data-id="element-101">
              Because responsible women deserve leverage. And leverage changes
              everything.
            </p>
          </div>
        </FadeIn>
      </div>
    </section>;
}